import { makePortraitAvatar } from "./avatars.ts";
import {
  DEMO_COMPANY_HOLDINGS_AG,
  DEMO_COMPANY_LABS_SP,
  DEMO_COMPANY_STUDIO_SRO,
  DEMO_COMPANY_TRADE_LTD,
  DEMO_COMPANY_VENTURES_GMBH,
  DEMO_USER_ID,
  DEMO_USER_USERNAME,
  type DemoCompanyId,
} from "./identifiers.ts";

export type DemoTeamMember = {
  id: string;
  partyRoleId: string;
  firstName: string;
  surname: string;
  email: string;
  phone?: string;
  jobTitle:
    | "FounderOwner"
    | "CEO"
    | "CFO"
    | "FinancialManager"
    | "Accountant"
    | "Other";
  jobTitleCustom?: string;
  // Memberships: which companies the person is part of + their role per company.
  memberships: {
    companyId: DemoCompanyId;
    role: "Admin" | "Manager" | "Member" | "ReadOnly";
  }[];
  status: "Active" | "Invited" | "Inactive";
  invitedAt?: string;
  lastActiveAt?: string;
  avatarUrl: string;
};

const make = (m: Omit<DemoTeamMember, "avatarUrl">): DemoTeamMember => ({
  ...m,
  avatarUrl: makePortraitAvatar(m.email),
});

export const DEMO_TEAM: DemoTeamMember[] = [
  make({
    id: DEMO_USER_ID,
    partyRoleId: "demo-role-anna-cfo",
    firstName: "Anna",
    surname: "Schmidt",
    email: DEMO_USER_USERNAME,
    phone: "+43 660 142 8800",
    jobTitle: "CFO",
    memberships: [
      { companyId: DEMO_COMPANY_HOLDINGS_AG, role: "Admin" },
      { companyId: DEMO_COMPANY_STUDIO_SRO, role: "Admin" },
      { companyId: DEMO_COMPANY_LABS_SP, role: "Admin" },
      { companyId: DEMO_COMPANY_VENTURES_GMBH, role: "Admin" },
      { companyId: DEMO_COMPANY_TRADE_LTD, role: "Member" },
    ],
    status: "Active",
    lastActiveAt: "2026-05-21T07:42:00.000Z",
  }),
  make({
    id: "demo-team-002",
    partyRoleId: "demo-role-team-002",
    firstName: "Lukas",
    surname: "Berger",
    email: "lukas.berger@contoso.demo",
    phone: "+43 660 110 1102",
    jobTitle: "CEO",
    jobTitleCustom: "Group CEO & Founder",
    memberships: [
      { companyId: DEMO_COMPANY_HOLDINGS_AG, role: "Admin" },
      { companyId: DEMO_COMPANY_STUDIO_SRO, role: "Admin" },
      { companyId: DEMO_COMPANY_LABS_SP, role: "Admin" },
      { companyId: DEMO_COMPANY_VENTURES_GMBH, role: "Admin" },
      { companyId: DEMO_COMPANY_TRADE_LTD, role: "Admin" },
    ],
    status: "Active",
    lastActiveAt: "2026-05-20T16:18:00.000Z",
  }),
  make({
    id: "demo-team-003",
    partyRoleId: "demo-role-team-003",
    firstName: "Sofia",
    surname: "Russo",
    email: "sofia.russo@contoso.demo",
    jobTitle: "FinancialManager",
    jobTitleCustom: "Group Controller",
    memberships: [
      { companyId: DEMO_COMPANY_HOLDINGS_AG, role: "Manager" },
      { companyId: DEMO_COMPANY_STUDIO_SRO, role: "Manager" },
      { companyId: DEMO_COMPANY_VENTURES_GMBH, role: "Manager" },
    ],
    status: "Active",
    lastActiveAt: "2026-05-21T06:05:00.000Z",
  }),
  make({
    id: "demo-team-004",
    partyRoleId: "demo-role-team-004",
    firstName: "Mateusz",
    surname: "Kowalski",
    email: "mateusz.kowalski@contoso.demo",
    jobTitle: "FinancialManager",
    jobTitleCustom: "Finance Manager — Contoso Labs",
    memberships: [{ companyId: DEMO_COMPANY_LABS_SP, role: "Admin" }],
    status: "Active",
    lastActiveAt: "2026-05-20T15:30:00.000Z",
  }),
  make({
    id: "demo-team-005",
    partyRoleId: "demo-role-team-005",
    firstName: "Petr",
    surname: "Veselý",
    email: "petr.vesely@contoso.demo",
    jobTitle: "FinancialManager",
    jobTitleCustom: "Finance Manager — Contoso Studio",
    memberships: [{ companyId: DEMO_COMPANY_STUDIO_SRO, role: "Admin" }],
    status: "Active",
    lastActiveAt: "2026-05-21T07:11:00.000Z",
  }),
  make({
    id: "demo-team-006",
    partyRoleId: "demo-role-team-006",
    firstName: "Elena",
    surname: "Vermeulen",
    email: "elena.vermeulen@contoso.demo",
    jobTitle: "Other",
    jobTitleCustom: "Treasury Manager",
    memberships: [
      { companyId: DEMO_COMPANY_HOLDINGS_AG, role: "Manager" },
      { companyId: DEMO_COMPANY_TRADE_LTD, role: "Manager" },
    ],
    status: "Active",
    lastActiveAt: "2026-05-21T08:02:00.000Z",
  }),
  make({
    id: "demo-team-007",
    partyRoleId: "demo-role-team-007",
    firstName: "Tomáš",
    surname: "Dvořák",
    email: "tomas.dvorak@contoso.demo",
    jobTitle: "Accountant",
    memberships: [
      { companyId: DEMO_COMPANY_STUDIO_SRO, role: "Member" },
      { companyId: DEMO_COMPANY_HOLDINGS_AG, role: "Member" },
    ],
    status: "Active",
    lastActiveAt: "2026-05-20T17:45:00.000Z",
  }),
  make({
    id: "demo-team-008",
    partyRoleId: "demo-role-team-008",
    firstName: "Małgorzata",
    surname: "Nowak",
    email: "malgorzata.nowak@contoso.demo",
    jobTitle: "Accountant",
    memberships: [{ companyId: DEMO_COMPANY_LABS_SP, role: "Member" }],
    status: "Active",
    lastActiveAt: "2026-05-20T14:20:00.000Z",
  }),
  make({
    id: "demo-team-009",
    partyRoleId: "demo-role-team-009",
    firstName: "Marc",
    surname: "Dubois",
    email: "marc.dubois@contoso.demo",
    jobTitle: "Other",
    jobTitleCustom: "Sales Manager",
    memberships: [
      { companyId: DEMO_COMPANY_HOLDINGS_AG, role: "Member" },
      { companyId: DEMO_COMPANY_TRADE_LTD, role: "Member" },
    ],
    status: "Active",
    lastActiveAt: "2026-05-20T11:38:00.000Z",
  }),
  make({
    id: "demo-team-010",
    partyRoleId: "demo-role-team-010",
    firstName: "Heinrich",
    surname: "Wagner",
    email: "heinrich.wagner@contoso.demo",
    jobTitle: "Other",
    jobTitleCustom: "Operations Manager",
    memberships: [
      { companyId: DEMO_COMPANY_VENTURES_GMBH, role: "Manager" },
      { companyId: DEMO_COMPANY_HOLDINGS_AG, role: "Member" },
    ],
    status: "Active",
    lastActiveAt: "2026-05-21T07:55:00.000Z",
  }),
  make({
    id: "demo-team-011",
    partyRoleId: "demo-role-team-011",
    firstName: "Olivia",
    surname: "Hughes",
    email: "olivia.hughes@contoso.demo",
    jobTitle: "Accountant",
    jobTitleCustom: "Bookkeeper — UK",
    memberships: [{ companyId: DEMO_COMPANY_TRADE_LTD, role: "Member" }],
    status: "Active",
    lastActiveAt: "2026-05-20T16:12:00.000Z",
  }),
  make({
    id: "demo-team-012",
    partyRoleId: "demo-role-team-012",
    firstName: "Anders",
    surname: "Lund",
    email: "anders.lund@contoso.demo",
    jobTitle: "Other",
    jobTitleCustom: "Junior Bookkeeper",
    memberships: [{ companyId: DEMO_COMPANY_VENTURES_GMBH, role: "Member" }],
    status: "Active",
    lastActiveAt: "2026-05-20T13:00:00.000Z",
  }),
  make({
    id: "demo-team-013",
    partyRoleId: "demo-role-team-013",
    firstName: "Júlia",
    surname: "Fernandes",
    email: "julia.fernandes@contoso.demo",
    jobTitle: "Other",
    jobTitleCustom: "Finance Intern",
    memberships: [{ companyId: DEMO_COMPANY_HOLDINGS_AG, role: "ReadOnly" }],
    status: "Active",
    lastActiveAt: "2026-05-19T16:30:00.000Z",
  }),
  make({
    id: "demo-team-014",
    partyRoleId: "demo-role-team-014",
    firstName: "Pavel",
    surname: "Bárta",
    email: "pavel.barta@externi-ucetni.demo",
    jobTitle: "Accountant",
    jobTitleCustom: "External Tax Advisor",
    memberships: [{ companyId: DEMO_COMPANY_STUDIO_SRO, role: "ReadOnly" }],
    status: "Active",
    lastActiveAt: "2026-05-15T10:00:00.000Z",
  }),
  make({
    id: "demo-team-015",
    partyRoleId: "demo-role-team-015",
    firstName: "Ines",
    surname: "Lehmann",
    email: "ines.lehmann@contoso.demo",
    jobTitle: "Accountant",
    memberships: [{ companyId: DEMO_COMPANY_VENTURES_GMBH, role: "Manager" }],
    status: "Invited",
    invitedAt: "2026-05-19T09:00:00.000Z",
  }),
];

export const DEMO_TEAM_BY_ID = Object.fromEntries(
  DEMO_TEAM.map((m) => [m.id, m]),
) as Record<string, DemoTeamMember>;

export function teamForCompany(companyId: DemoCompanyId): DemoTeamMember[] {
  return DEMO_TEAM.filter((m) =>
    m.memberships.some((mm) => mm.companyId === companyId),
  );
}
